// Fachsprache und Aufruf der Organisationsnamen-Pflege (ST-PTY-003, zweiter
// Schnitt). REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// contact-points.ts und identifiers.ts.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

export type CommandResult =
  | {
      readonly outcome: 'applied';
      readonly displayName: string;
      readonly legalName: string;
      readonly tradeName?: string;
      readonly lockVersion: number;
    }
  | { readonly outcome: 'invalid'; readonly errors: readonly { readonly fieldPath: string; readonly reasonCode: string }[] }
  | { readonly outcome: 'conflict'; readonly message: PlainMessage }
  | { readonly outcome: 'not-found'; readonly message: PlainMessage }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export const CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Jemand anderes war schneller.',
  explanation: 'Die Namen dieser Organisation haben sich geändert, seit du diese Seite geöffnet hast. Deine Eingabe wurde nicht gespeichert.',
  nextStep: 'Lade das Profil neu und entscheide dann noch einmal.',
  retryable: false,
});

export const NOT_FOUND_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Organisation gibt es hier nicht.',
  // KEINE VERMUTUNG ueber eine Berechtigung -- derselbe Ausgang wie ueberall.
  explanation: 'Sie wurde entfernt, oder die Adresse ist nicht mehr gültig.',
  nextStep: 'Geh zurück zur Kontaktliste und öffne den Kontakt dort erneut.',
  retryable: false,
});

/** Der Satz zu einer abgewiesenen Eingabe, je Feld und Grund (NFR-UX-005). */
export function validationMessageFor(fieldPath: string, reasonCode: string): string {
  if (fieldPath === 'organization.legalName') {
    if (reasonCode === 'field_too_long') {
      return 'Der Rechtsname ist zu lang.';
    }
    return 'Trage den Rechtsnamen ein — ohne ihn gibt es die Organisation nicht.';
  }
  if (fieldPath === 'organization.tradeName') {
    if (reasonCode === 'field_too_long') {
      return 'Der Handelsname ist zu lang.';
    }
    return 'Prüfe den Handelsnamen.';
  }
  return 'Prüfe die markierte Angabe.';
}

/**
 * Die Bestaetigung nennt die WIRKUNG: welcher Name ab jetzt in Listen steht.
 * Genau das ist die Folge, die man sonst erst beim naechsten Suchen bemerkt.
 */
export function confirmationFor(result: { readonly displayName: string; readonly tradeName?: string }): string {
  return result.tradeName === undefined
    ? `Gespeichert. In Listen erscheint diese Organisation als „${result.displayName}“ (ihr Rechtsname — es ist kein Handelsname geführt).`
    : `Gespeichert. In Listen erscheint diese Organisation als „${result.displayName}“ (ihr Handelsname).`;
}

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return {
    status: response.status,
    answered: payload !== undefined,
    ...correlationOf(body),
  };
}

export interface CommandOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}

export async function renameOrganization(
  options: CommandOptions & { readonly legalName: string; readonly tradeName: string; readonly lockVersion: number },
): Promise<CommandResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/organization-name`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${options.accessToken}` },
    body: JSON.stringify({
      organization: { legalName: options.legalName, tradeName: options.tradeName },
      lockVersion: options.lockVersion,
    }),
  });
  const payload: unknown = await response.json().catch(() => undefined);
  const record = (payload ?? {}) as Record<string, unknown>;

  if (response.ok) {
    const organization = (record.organization ?? {}) as Record<string, unknown>;
    return {
      outcome: 'applied',
      displayName: typeof record.displayName === 'string' ? record.displayName : '',
      legalName: typeof organization.legalName === 'string' ? organization.legalName : '',
      ...(typeof organization.tradeName === 'string' ? { tradeName: organization.tradeName } : {}),
      lockVersion: typeof organization.lockVersion === 'number' ? organization.lockVersion : 0,
    };
  }
  if (response.status === 404) {
    return { outcome: 'not-found', message: NOT_FOUND_MESSAGE };
  }
  if (response.status === 409) {
    return { outcome: 'conflict', message: CONFLICT_MESSAGE };
  }
  if (response.status === 422 && Array.isArray(record.errors)) {
    return {
      outcome: 'invalid',
      errors: record.errors.flatMap((entry: unknown) => {
        const row = (entry ?? {}) as Record<string, unknown>;
        return typeof row.fieldPath === 'string' && typeof row.reasonCode === 'string'
          ? [{ fieldPath: row.fieldPath, reasonCode: row.reasonCode }]
          : [];
      }),
    };
  }
  return {
    outcome: 'rejected',
    message: plainMessageFor(typeof record.reasonCode === 'string' ? record.reasonCode : undefined, factsOf(response, payload)),
  };
}
